import { useState, useEffect, useCallback, useRef } from "react";
import { TextField, FormControlLabel, Checkbox, Switch, Typography, Box, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faPlus, faTrash, faPencil, faSpinner, faChevronDown, faBell } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import "./warmLeadConfig.css";
import "./notificationCenter.css";
import "./liveInbox.css";
import { useTranslation } from "react-i18next";
import "../translations.js";
import AddParameterModal from "./addParameterModal";
import ConfirmationModal from "./confirmationModal";
import InfoTooltip from "./InfoTooltip";
import { useUnsavedChanges } from "../contexts/UnsavedChangesContext.js";
import { guardWrite } from "../utils/permissions";

const ACCORDION_SX = {
  background: "transparent",
  boxShadow: "none",
  border: "none",
  borderRadius: 0,
  "&:before": { display: "none" },
  marginTop: 0,
  marginBottom: "20px",
  paddingBottom: "20px",
  borderBottom: "1.5px solid #e0e0e0",
  "&.Mui-expanded": {
    marginTop: 0,
    marginBottom: "20px",
  },
  "&.warm-lead-accordion-last": {
    borderBottom: "none",
    marginBottom: 0,
    paddingBottom: 0,
    "&.Mui-expanded": {
      marginBottom: 0,
    },
  },
};

const ACCORDION_SUMMARY_SX = {
  padding: 0,
  minHeight: "unset",
  transition: "margin-bottom 0.3s ease",
  "& .MuiAccordionSummary-content": {
    margin: 0,
    "&.Mui-expanded": { margin: 0 },
  },
  "&.Mui-expanded": { minHeight: "unset", marginBottom: "10px" },
  "& .MuiAccordionSummary-expandIconWrapper": {
    color: "#5616ea",
    fontSize: "18px",
  },
};

const ACCORDION_DETAILS_SX = {
  padding: 0,
  paddingTop: "0px",
};

const WarmLeadConfig = ({ botId, onUnsavedStateChange, onUnsavedHandlersChange, preview = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { registerUnsavedChangesGuard } = useUnsavedChanges();
  const [loading, setLoading] = useState(true);
  const [isLeadActive, setIsLeadActive] = useState(false);
  const [emails, setEmails] = useState([""]);
  const [botFunctionId, setBotFunctionId] = useState(null);
  const [parameters, setParameters] = useState([]);
  const [initialIsLeadActive, setInitialIsLeadActive] = useState(false);
  const [initialEmails, setInitialEmails] = useState([""]);
  const [initialParameters, setInitialParameters] = useState([]);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [editingParameterId, setEditingParameterId] = useState(null);
  const [isAddParameterModalOpen, setIsAddParameterModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [parameterToDelete, setParameterToDelete] = useState(null);
  const editingParamRef = useRef(null);

  const [requirePreChat, setRequirePreChat] = useState(false);
  const [initialRequirePreChat, setInitialRequirePreChat] = useState(false);
  const [requiredFields, setRequiredFields] = useState([]);
  const [initialRequiredFields, setInitialRequiredFields] = useState([]);

  // Persist accordion open/close state per bot across tab switches.
  // Default: first accordion (lead parameters) open if no saved state.
  const accordionStorageKey = botId ? `warmLeadAccordions:${botId}` : null;
  const DEFAULT_EXPANDED_ACCORDIONS = { notifications: true, leadParameters: true, automaticLeadCapture: true, enforceContactDetails: true };
  const [expandedAccordions, setExpandedAccordions] = useState(() => {
    if (!accordionStorageKey) return { ...DEFAULT_EXPANDED_ACCORDIONS };
    try {
      const raw = localStorage.getItem(accordionStorageKey);
      if (raw) return JSON.parse(raw) || { ...DEFAULT_EXPANDED_ACCORDIONS };
    } catch (_e) { /* noop */ }
    return { ...DEFAULT_EXPANDED_ACCORDIONS };
  });

  useEffect(() => {
    if (!accordionStorageKey) return;
    try {
      localStorage.setItem(accordionStorageKey, JSON.stringify(expandedAccordions));
    } catch (_e) { /* noop */ }
  }, [accordionStorageKey, expandedAccordions]);

  const toggleAccordion = (key) => (_e, isExpanded) => {
    setExpandedAccordions((prev) => ({ ...prev, [key]: isExpanded }));
  };

  const fetchWarmLeadFunction = useCallback(
    async (botId) => {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}warm_lead_function/${botId}`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();

        const activeFromServer = data.active;
        const emailsFromServer = data.email || [""];
        const parametersFromServer = data.bot_function_parameters.map(param => ({
          id: param.id,
          name: param.name,
          description: param.description,
          active: param.active,
          type: param.type,
          required: param.required,
          enum: param.enum,
          user_display: param.user_display || ''
        }));
        const collectionModeFromServer = data.collection_mode || 'optional';
        const requirePreChatFromServer = collectionModeFromServer === 'required';
        const requiredFieldsFromServer = data.required_fields || [];

        setIsLeadActive(activeFromServer);
        setEmails(emailsFromServer);
        setBotFunctionId(data.id);
        setParameters(parametersFromServer);
        setRequirePreChat(requirePreChatFromServer);
        setRequiredFields(requiredFieldsFromServer);

        setInitialIsLeadActive(activeFromServer);
        setInitialEmails(emailsFromServer);
        setInitialParameters(JSON.parse(JSON.stringify(parametersFromServer)));
        setInitialRequirePreChat(requirePreChatFromServer);
        setInitialRequiredFields([...requiredFieldsFromServer]);

        setHasLoadedInitial(true);
      } catch (err) {
        console.error("Error fetching warm lead function data:", err);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (preview) {
      // The Lead Generation page renders this config only as a blurred, inert
      // backdrop, so skip the network call entirely and show representative
      // dummy data — the owner never reads it through the blur.
      setIsLeadActive(true);
      setEmails(["you@example.com"]);
      setParameters([
        { id: 1, name: "name", description: "The full name of the user.", active: true, type: "string", required: true, enum: "", user_display: "Name" },
        { id: 2, name: "email", description: "The email address of the user.", active: true, type: "string", required: true, enum: "", user_display: "Email" },
        { id: 3, name: "phone_number", description: "The phone number of the user.", active: false, type: "string", required: false, enum: "", user_display: "Phone" },
      ]);
      setRequirePreChat(false);
      setRequiredFields([]);
      setLoading(false);
      return;
    }
    if (botId) {
      fetchWarmLeadFunction(botId);
    }
  }, [botId, fetchWarmLeadFunction, preview]);

  useEffect(() => {
    if (editingParameterId == null) return;
    const handleClickOutside = (e) => {
      if (editingParamRef.current && !editingParamRef.current.contains(e.target)) {
        setEditingParameterId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [editingParameterId]);

  useEffect(() => {
    if (!hasLoadedInitial) {
      return;
    }
    const isEmailsChanged =
      JSON.stringify(initialEmails) !== JSON.stringify(emails);
    const isParametersChanged =
      JSON.stringify(initialParameters) !== JSON.stringify(parameters);
    const isRequirePreChatChanged = initialRequirePreChat !== requirePreChat;
    const isRequiredFieldsChanged =
      JSON.stringify(initialRequiredFields.sort()) !== JSON.stringify([...requiredFields].sort());

    setHasUnsavedChanges(
      isEmailsChanged || isParametersChanged || isRequirePreChatChanged || isRequiredFieldsChanged
    );
  }, [emails, parameters, initialEmails, initialParameters, hasLoadedInitial, requirePreChat, initialRequirePreChat, requiredFields, initialRequiredFields]);

  const handleLeadToggle = async (isActive) => {
    try {
      const payload = { active: isActive };
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}warm_lead_function/${botId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
    } catch (err) {
      console.error("Error updating lead function activation status:", err);
    }
  };

  const handlePreChatToggle = async (isEnabled, activeParamIds) => {
    try {
      const payload = {
        collection_mode: isEnabled ? 'required' : 'optional',
        required_fields: activeParamIds,
      };
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}warm_lead_function/${botId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
    } catch (err) {
      console.error("Error updating pre-chat requirement status:", err);
    }
  };

  const handleSave = async () => {
    // Read-only teammates: revert local edits and surface the shared
    // permission modal instead of attempting the PUT (which would 403).
    if (!guardWrite(() => handleDiscardChanges())) return;
    try {
      setLoading(true);
      const payload = {
        bot_function_id: botFunctionId,
        active: isLeadActive,
        email: emails,
        collection_mode: requirePreChat ? 'required' : 'optional',
        required_fields: requiredFields,
        bot_function_parameters: parameters.map(param => ({
          id: param.id,
          name: param.name,
          description: param.description,
          active: param.active,
          user_display: param.user_display || null
        })),
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}warm_lead_function/${botId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      setInitialIsLeadActive(isLeadActive);
      setInitialEmails(emails);
      setInitialParameters(JSON.parse(JSON.stringify(parameters)));
      setInitialRequirePreChat(requirePreChat);
      setInitialRequiredFields([...requiredFields]);
      setHasUnsavedChanges(false);
      setEditingParameterId(null);
      return true;
    } catch (err) {
      console.error("Error saving settings:", err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleDiscardChanges = () => {
    setIsLeadActive(initialIsLeadActive);
    setEmails([...initialEmails]);
    setParameters(JSON.parse(JSON.stringify(initialParameters)));
    setRequirePreChat(initialRequirePreChat);
    setRequiredFields([...initialRequiredFields]);
    setEditingParameterId(null);
  };

  useEffect(() => {
    if (onUnsavedStateChange) onUnsavedStateChange(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedStateChange]);

  // Keep latest handlers in a ref so guard registration doesn't re-run on every keystroke.
  const unsavedHandlersRef = useRef({ save: handleSave, discard: handleDiscardChanges });
  unsavedHandlersRef.current = { save: handleSave, discard: handleDiscardChanges };

  useEffect(() => {
    if (!onUnsavedHandlersChange) return undefined;
    onUnsavedHandlersChange({
      save: (...args) => unsavedHandlersRef.current.save(...args),
      discard: (...args) => unsavedHandlersRef.current.discard(...args),
    });
    return () => onUnsavedHandlersChange(null);
  }, [onUnsavedHandlersChange]);

  useEffect(() => {
    return registerUnsavedChangesGuard(`warm-lead-config-${botId}`, {
      when: hasUnsavedChanges,
      onSave: (...args) => unsavedHandlersRef.current.save(...args),
      onDiscard: (...args) => unsavedHandlersRef.current.discard(...args),
    });
  }, [botId, hasUnsavedChanges, registerUnsavedChangesGuard]);

  const handleRequiredFieldToggle = (paramId) => {
    if (requiredFields.includes(paramId)) {
      setRequiredFields(requiredFields.filter(id => id !== paramId));
    } else {
      setRequiredFields([...requiredFields, paramId]);
    }
  };

  const handleParameterToggle = (paramId) => {
    setParameters(parameters.map(param =>
      param.id === paramId ? { ...param, active: !param.active } : param
    ));
  };

  const handleParameterNameChange = (paramId, newName) => {
    const sanitizedName = newName
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z_]/g, '')
      .replace(/_+/g, '_');
    setParameters(parameters.map(param =>
      param.id === paramId ? { ...param, name: sanitizedName } : param
    ));
  };

  const handleParameterDescriptionChange = (paramId, newDescription) => {
    setParameters(parameters.map(param =>
      param.id === paramId ? { ...param, description: newDescription } : param
    ));
  };

  const handleParameterUserDisplayChange = (paramId, newUserDisplay) => {
    setParameters(parameters.map(param =>
      param.id === paramId ? { ...param, user_display: newUserDisplay } : param
    ));
  };

  const handleAddCustomParameter = async (formData) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}warm_lead_function/${botId}/parameter`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: formData.name,
            description: formData.description,
            user_display: formData.user_display || null,
            type: "string",
            required: true,
            active: true
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      await fetchWarmLeadFunction(botId);
    } catch (err) {
      console.error("Error adding custom parameter:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteParameter = async (paramId) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}warm_lead_function/parameter/${paramId}?bot_id=${botId}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      await fetchWarmLeadFunction(botId);
    } catch (err) {
      console.error("Error deleting parameter:", err);
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (paramId) => {
    setParameterToDelete(paramId);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (parameterToDelete) {
      await handleDeleteParameter(parameterToDelete);
      setParameterToDelete(null);
    }
  };

  const handleEmailChange = (index, value) => {
    const updatedEmails = [...emails];
    updatedEmails[index] = value;
    setEmails(updatedEmails);
  };

  const handleAddEmail = () => {
    setEmails([...emails, ""]);
  };

  const handleRemoveEmail = (index) => {
    const updatedEmails = emails.filter((_, i) => i !== index);
    setEmails(updatedEmails);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <FontAwesomeIcon icon={faSpinner} spin className="main-loading-spinner" />
      </div>
    );
  }

  return (
    <div className="warm-lead-parent-container">
      <div className="warm-lead-info-box">
        <FontAwesomeIcon className="warm-lead-info-icon" icon={faCircleInfo} />
        <div className="warm-lead-info-content">
          <p>
            {t("warm_lead_info_box")}
          </p>
        </div>
      </div>

      <div className="warm-lead-container">
        <Accordion expanded={!!expandedAccordions.notifications} onChange={toggleAccordion("notifications")} disableGutters elevation={0} sx={ACCORDION_SX}>
          <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />} sx={ACCORDION_SUMMARY_SX}>
            <h4 className="widget-sub-header" style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>
              {t("live_inbox_notifications_title") || "Notifications"}
            </h4>
          </AccordionSummary>
          <AccordionDetails sx={ACCORDION_DETAILS_SX}>
            <Box className="control-group" style={{ marginBottom: '0px' }}>
              <span className="live-inbox-bot-desc" style={{ display: 'block', marginBottom: '12px' }}>
                {t("live_inbox_notifications_desc_leads") || "Manage how to get notified about new leads."}
              </span>
              <button
                type="button"
                className="live-inbox-toggle online"
                style={{ width: 'auto', alignSelf: 'flex-start' }}
                onClick={() => window.dispatchEvent(new CustomEvent('bot-settings-set-tab', { detail: { tab: 'notifications' } }))}
              >
                <FontAwesomeIcon icon={faBell} />
                <span>{t('live_inbox_manage_notifications') || 'Manage Notifications'}</span>
              </button>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* 1. Lead Parameters Section */}
        <Accordion expanded={!!expandedAccordions.leadParameters} onChange={toggleAccordion("leadParameters")} disableGutters elevation={0} sx={ACCORDION_SX}>
          <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />} sx={ACCORDION_SUMMARY_SX}>
            <h4 className="widget-sub-header" style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>
              {t("lead-parameters-header")}
              <InfoTooltip text={t("warm_lead_parameters_tooltip")} />
            </h4>
          </AccordionSummary>
          <AccordionDetails sx={ACCORDION_DETAILS_SX}>
        <Box className="control-group">

          {parameters.map((param) => (
            <div key={param.id} className="parameter-item" ref={editingParameterId === param.id ? editingParamRef : undefined}>
              <Box display="flex" alignItems="center" gap={1} className="parameter-row">
                <div className="parameter-content">
                  {editingParameterId === param.id ? (
                    <>
                      <TextField
                        value={param.name}
                        onChange={(e) => handleParameterNameChange(param.id, e.target.value)}
                        size="small"
                        label={t('parameter_name')}
                        placeholder={t("parameter_name_ph")}
                        className="parameter-name-input"
                        InputProps={{
                          sx: { height: '44px' }
                        }}
                        sx={{
                          marginBottom: '8px',
                          "& .MuiOutlinedInput-root.Mui-focused": {
                            "& fieldset": {
                              border: "1px solid #5616ea",
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: 'gray',
                            '&.Mui-focused': { color: '#5616ea' },
                          },
                        }}
                      />
                      <TextField
                        value={param.description}
                        onChange={(e) => handleParameterDescriptionChange(param.id, e.target.value)}
                        size="small"
                        label={t('parameter_description')}
                        placeholder={t("parameter_description_ph")}
                        className="parameter-description-input"
                        InputProps={{
                          sx: { height: '44px' }
                        }}
                        sx={{
                          marginBottom: '8px',
                          "& .MuiOutlinedInput-root.Mui-focused": {
                            "& fieldset": {
                              border: "1px solid #5616ea",
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: 'gray',
                            '&.Mui-focused': { color: '#5616ea' },
                          },
                        }}
                      />
                      <TextField
                        value={param.user_display || ''}
                        onChange={(e) => handleParameterUserDisplayChange(param.id, e.target.value)}
                        size="small"
                        label={t('parameter_user_display')}
                        placeholder={t("parameter_user_display_ph")}
                        className="parameter-user-display-input"
                        InputProps={{
                          sx: { height: '44px' }
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root.Mui-focused": {
                            "& fieldset": {
                              border: "1px solid #5616ea",
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: 'gray',
                            '&.Mui-focused': { color: '#5616ea' },
                          },
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <Typography className="parameter-name">
                        {param.name}
                        {param.user_display && (
                          <span className="parameter-user-display-badge">{param.user_display}</span>
                        )}
                      </Typography>
                      <Typography className="parameter-description">{param.description}</Typography>
                    </>
                  )}
                </div>
                <div className="parameter-actions">
                  <button
                    onClick={() => setEditingParameterId(editingParameterId === param.id ? null : param.id)}
                    className="edit-parameter-button"
                    title={editingParameterId === param.id ? t("parameter_done_editing") : t("parameter_edit")}
                  >
                    <FontAwesomeIcon icon={faPencil} />
                  </button>
                  <button
                    onClick={() => openDeleteModal(param.id)}
                    className="delete-parameter-button"
                    title={t("parameter_delete")}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </Box>
            </div>
          ))}

          <button
            onClick={() => setIsAddParameterModalOpen(true)}
            className="add-parameter-button-icon"
            title={t("add_parameter")}
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </Box>
          </AccordionDetails>
        </Accordion>

        {/* 2. Automatic Lead Capture Section */}
        <Accordion expanded={!!expandedAccordions.automaticLeadCapture} onChange={toggleAccordion("automaticLeadCapture")} disableGutters elevation={0} sx={ACCORDION_SX}>
          <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />} sx={ACCORDION_SUMMARY_SX}>
            <h4 className="widget-sub-header" style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>
              {t("automatic-lead-capture-header")}
              <InfoTooltip text={t("warm_lead_auto_capture_tooltip")} />
            </h4>
          </AccordionSummary>
          <AccordionDetails sx={ACCORDION_DETAILS_SX}>
        <Box className="control-group">

          <Box display="flex" alignItems="center" gap={1.5}>
            <FormControlLabel
              control={
                <Switch
                  checked={isLeadActive}
                  onChange={(e) => {
                    // Read-only teammates: don't visually flip the switch —
                    // surface the unified permission modal and bail.
                    if (!guardWrite()) return;
                    setIsLeadActive(e.target.checked);
                    handleLeadToggle(e.target.checked);
                  }}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#5616ea',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#5616ea',
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: '#ccc',
                    },
                  }}
                />
              }
              label={t("activate-lead-function")}
            />
          </Box>

          {/* Notification Receiver - shown when lead capture is active */}
          {isLeadActive && (
            <Box>
              <Box className="required-fields-section">
                <Typography variant="body2" fontWeight={600} color="textPrimary" sx={{ marginBottom: '12px' }}>
                  {t("select-auto-capture-fields")}
                </Typography>
                {parameters.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" fontStyle="italic">
                    {t("no-active-parameters")}
                  </Typography>
                ) : (
                  <Box className="required-fields-list">
                    {parameters.map((param) => (
                      <FormControlLabel
                        key={param.id}
                        control={
                          <Checkbox
                            checked={param.active}
                            onChange={() => handleParameterToggle(param.id)}
                            sx={{
                              '&.Mui-checked': { color: '#5616ea' },
                              '&:hover': { backgroundColor: '#5616ea15' },
                            }}
                          />
                        }
                        label={param.user_display || param.name}
                        className="required-field-item"
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
          </AccordionDetails>
        </Accordion>

        {/* 3. Enforce Contact Details Section */}
        <Accordion className="warm-lead-accordion-last" expanded={!!expandedAccordions.enforceContactDetails} onChange={toggleAccordion("enforceContactDetails")} disableGutters elevation={0} sx={ACCORDION_SX}>
          <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />} sx={ACCORDION_SUMMARY_SX}>
            <h4 className="widget-sub-header" style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>
              {t("enforce-contact-details-header")}
              <InfoTooltip text={t("warm_lead_enforce_details_tooltip")} />
              {/* <span className="warm-lead-new-badge-inline">New</span> */}
            </h4>
          </AccordionSummary>
          <AccordionDetails sx={ACCORDION_DETAILS_SX}>
        <Box className="control-group">

          <Box display="flex" alignItems="center" gap={1.5}>
            <FormControlLabel
              control={
                <Switch
                  checked={requirePreChat}
                  onChange={(e) => {
                    // Read-only teammates: don't visually flip the switch —
                    // surface the unified permission modal and bail.
                    if (!guardWrite()) return;
                    const isEnabled = e.target.checked;
                    setRequirePreChat(isEnabled);
                    // Update initial state IMMEDIATELY to prevent flicker
                    setInitialRequirePreChat(isEnabled);

                    // Auto-select all active parameters when enabling
                    const activeParamIds = isEnabled
                      ? parameters.filter(p => p.active).map(p => p.id)
                      : requiredFields;
                    if (isEnabled) {
                      setRequiredFields(activeParamIds);
                      setInitialRequiredFields(activeParamIds);
                    }
                    // Save to backend
                    handlePreChatToggle(isEnabled, isEnabled ? activeParamIds : requiredFields);
                  }}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#5616ea',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#5616ea',
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: '#ccc',
                    },
                  }}
                />
              }
              label={t("require-pre-chat")}
            />
          </Box>

          {/* Show required fields selector when pre-chat is enabled */}
          {requirePreChat && (
            <Box>
              <Box className="required-fields-section">
                <Typography variant="body2" fontWeight={600} color="textPrimary" sx={{ marginBottom: '12px' }}>
                  {t("select-required-fields")}
                </Typography>
                {parameters.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" fontStyle="italic">
                    {t("no-active-parameters")}
                  </Typography>
                ) : (
                  <Box className="required-fields-list">
                    {parameters.map((param) => (
                      <FormControlLabel
                        key={param.id}
                        control={
                          <Checkbox
                            checked={requiredFields.includes(param.id)}
                            onChange={() => handleRequiredFieldToggle(param.id)}
                            sx={{
                              '&.Mui-checked': { color: '#5616ea' },
                              '&:hover': { backgroundColor: '#5616ea15' },
                            }}
                          />
                        }
                        label={param.user_display || param.name}
                        className="required-field-item"
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
          </AccordionDetails>
        </Accordion>

        <AddParameterModal
          open={isAddParameterModalOpen}
          onClose={() => setIsAddParameterModalOpen(false)}
          onSubmit={handleAddCustomParameter}
        />

        <ConfirmationModal
          open={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setParameterToDelete(null);
          }}
          onConfirm={confirmDelete}
          itemName={parameters.find(p => p.id === parameterToDelete)?.name || t("warm_lead_this_parameter")}
          requireTextConfirmation={false}
        />

        {hasUnsavedChanges && (
          <div className="unsaved-changes-message">
            <div className="warning-icon">⚠️</div>
            <div className="warning-text">{t("unsaved_changes_info")}</div>
          </div>
        )}
        <div className="save-discard-buttons">
          <button className="save-widget-btn" onClick={handleSave}>
            {t("save")}
          </button>
          {hasUnsavedChanges && (
            <button
              onClick={handleDiscardChanges}
              className="discard-widget-btn"
            >
              {t("discard_changes")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WarmLeadConfig;
